import { queryOptions } from "@tanstack/react-query";
import {
  CallAudioDownloadManifest,
  CallAudioDownloadPreview,
  CallAudioPlayback,
  CallAudioRecordingsCount,
  CallAudioRecordingsResponse,
  CallAudioSuggestionsResponse,
  CallAudioTranscript,
  type CallAudioDownloadManifest as CallAudioDownloadManifestBody,
  type CallAudioDownloadPreview as CallAudioDownloadPreviewBody,
  type CallAudioHandlerRole,
  type CallAudioHandlerRoleKey,
  type CallAudioPageSize,
  type CallAudioPlayback as CallAudioPlaybackBody,
  type CallAudioRecordingsCount as CallAudioRecordingsCountBody,
  type CallAudioRecordingsResponse as CallAudioRecordingsBody,
  type CallAudioSortColumn,
  type CallAudioSource,
  type CallAudioSuggestionField,
  type CallAudioSuggestionsResponse as CallAudioSuggestionsBody,
  type CallAudioTranscript as CallAudioTranscriptBody,
} from "@t3tools/contracts";
import { Schema } from "effect";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";

export type CallAudioPageRequest = {
  cursor: string | null;
  pageDirection: "first" | "next" | "prev" | "last";
};

export type CallAudioRecordingFilters = {
  callDirections: Array<"inbound" | "outbound">;
  callTypes: string[];
  caregivers: string[];
  carespaceIds: string[];
  clinics: string[];
  durationMaxMinutes?: string;
  durationMinMinutes?: string;
  end: string;
  externalNumbers: string[];
  handlerRoles: CallAudioHandlerRoleKey[];
  navigators: string[];
  pageSize: CallAudioPageSize;
  recordingTypes: Array<"callrecording" | "admincallrecording">;
  sort?: CallAudioSortColumn;
  sortDirection?: "asc" | "desc";
  sources: CallAudioSource[];
  start: string;
  transcripts: Array<"true" | "false">;
};

export const CALL_AUDIO_API_HEADER = "x-operators-call-audio-api";
export const CALL_AUDIO_API_VERSION = "2";
const CALL_AUDIO_RELOAD_KEY = "operators.call-audio.api-reload";
const listKey = ["dashboards", "call-audio", "recordings"] as const;

export function cycleCallAudioSort(
  filters: CallAudioRecordingFilters,
  column: CallAudioSortColumn,
): CallAudioRecordingFilters {
  if (filters.sort !== column) return { ...filters, sort: column, sortDirection: "asc" };
  if (filters.sortDirection === "asc") return { ...filters, sortDirection: "desc" };
  const { sort: _sort, sortDirection: _direction, ...rest } = filters;
  return rest;
}

export function callAudioRecordingsSearchParams(
  filters: CallAudioRecordingFilters,
  page: CallAudioPageRequest = { cursor: null, pageDirection: "first" },
  fresh = false,
): URLSearchParams {
  const params = new URLSearchParams({
    pageSize: String(filters.pageSize),
  });
  if (filters.start) params.set("start", filters.start);
  if (filters.end) params.set("end", filters.end);
  if (page.pageDirection !== "first") params.set("pageDirection", page.pageDirection);
  if (page.cursor) params.set("cursor", page.cursor);
  appendAll(params, "navigator", filters.navigators);
  appendAll(params, "handlerRole", filters.handlerRoles);
  appendAll(params, "carespaceId", filters.carespaceIds);
  appendAll(params, "clinic", filters.clinics);
  appendAll(params, "caregiver", filters.caregivers);
  appendAll(params, "externalNumber", filters.externalNumbers);
  appendAll(params, "callType", filters.callTypes);
  appendAll(params, "source", filters.sources);
  appendAll(params, "callDirection", filters.callDirections);
  appendAll(params, "recordingType", filters.recordingTypes);
  const transcript = filters.transcripts.length === 1 ? filters.transcripts[0] : undefined;
  if (transcript) params.set("hasTranscript", transcript);
  const min = filters.durationMinMinutes?.trim();
  const max = filters.durationMaxMinutes?.trim();
  if (min) params.set("durationMinMinutes", min);
  if (max) params.set("durationMaxMinutes", max);
  if (filters.sort && filters.sortDirection) {
    params.set("sort", filters.sort);
    params.set("direction", filters.sortDirection);
  }
  if (fresh) params.set("fresh", "1");
  return params;
}

export async function fetchCallAudioRecordings(
  filters: CallAudioRecordingFilters,
  page: CallAudioPageRequest = { cursor: null, pageDirection: "first" },
  fresh = false,
): Promise<CallAudioRecordingsBody> {
  const params = callAudioRecordingsSearchParams(filters, page, fresh);
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/recordings?${params}`,
    "Failed to load call audio recordings",
    fresh,
    true,
  );
  if (!Schema.is(CallAudioRecordingsResponse)(body)) {
    throw new Error("Call audio recordings response was malformed");
  }
  return body;
}

export function callAudioRecordingsQueryOptions(
  filters: CallAudioRecordingFilters,
  page: CallAudioPageRequest,
  takeFresh: () => boolean,
) {
  return queryOptions({
    placeholderData: (previous) => previous,
    queryFn: () => fetchCallAudioRecordings(filters, page, takeFresh()),
    queryKey: [...operatorsQueryKeys.all, ...listKey, filters, page] as const,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export async function fetchCallAudioRecordingsCount(
  filters: CallAudioRecordingFilters,
  fresh = false,
): Promise<CallAudioRecordingsCountBody> {
  const params = callAudioRecordingsSearchParams(
    filters,
    { cursor: null, pageDirection: "first" },
    fresh,
  );
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/recordings/count?${params}`,
    "Failed to count call audio recordings",
    fresh,
    true,
  );
  if (!Schema.is(CallAudioRecordingsCount)(body)) {
    throw new Error("Call audio count response was malformed");
  }
  return body;
}

export function callAudioRecordingsCountQueryOptions(
  filters: CallAudioRecordingFilters,
  takeFresh: () => boolean,
) {
  return queryOptions({
    queryFn: () => fetchCallAudioRecordingsCount(filters, takeFresh()),
    queryKey: [...operatorsQueryKeys.all, ...listKey, "count", filters] as const,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export type CallAudioDownloadIncludes = {
  audio: boolean;
  transcripts: boolean;
};

function callAudioDownloadSearchParams(
  filters: CallAudioRecordingFilters,
  include: CallAudioDownloadIncludes,
): URLSearchParams {
  const params = callAudioRecordingsSearchParams(filters);
  params.delete("cursor");
  params.delete("pageDirection");
  params.set("includeAudio", include.audio ? "true" : "false");
  params.set("includeTranscripts", include.transcripts ? "true" : "false");
  return params;
}

export async function fetchCallAudioDownloadPreview(
  filters: CallAudioRecordingFilters,
  include: CallAudioDownloadIncludes,
): Promise<CallAudioDownloadPreviewBody> {
  const params = callAudioDownloadSearchParams(filters, include);
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/recordings/download-preview?${params}`,
    "Failed to prepare the call audio download",
    true,
  );
  if (!Schema.is(CallAudioDownloadPreview)(body)) {
    throw new Error("Call audio download preview was malformed");
  }
  return body;
}

export function callAudioDownloadPreviewQueryOptions(
  filters: CallAudioRecordingFilters,
  include: CallAudioDownloadIncludes,
  enabled: boolean,
) {
  return queryOptions({
    enabled,
    gcTime: 0,
    queryFn: () => fetchCallAudioDownloadPreview(filters, include),
    queryKey: [
      ...operatorsQueryKeys.all,
      ...listKey,
      "download-preview",
      filters,
      include,
    ] as const,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
}

export async function fetchCallAudioDownloadManifest(
  filters: CallAudioRecordingFilters,
  include: { audio: boolean; transcripts: boolean },
): Promise<CallAudioDownloadManifestBody> {
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/recordings/download-manifest?${callAudioDownloadSearchParams(filters, include).toString()}`,
    "Failed to prepare the call audio download",
    true,
    true,
  );
  if (!Schema.is(CallAudioDownloadManifest)(body)) {
    throw new Error("Call audio download response was malformed");
  }
  return body;
}

export async function fetchCallAudioDownload(
  filters: CallAudioRecordingFilters,
  include: CallAudioDownloadIncludes,
): Promise<Blob> {
  const params = callAudioDownloadSearchParams(filters, include);
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/call-audio/recordings/download?${params}`,
    { cache: "no-store", credentials: "include" },
  );
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.status === 403) {
    throw new OperatorsForbiddenError("You do not have access to Dashboards.");
  }
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(() => "Failed to download call audio"),
    );
  }
  if (!response.body) throw new Error("Download interrupted");
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(value);
    }
  } catch {
    throw new Error("Download interrupted");
  }
  return new Blob(chunks as BlobPart[], { type: "application/zip" });
}

/** Saves the ZIP after the click, so revoking the URL does not cancel the download. */
export function saveCallAudioDownload(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "call-audio.zip";
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function fetchCallAudioSuggestions(input: {
  end: string;
  field: CallAudioSuggestionField;
  q: string;
  start: string;
}): Promise<CallAudioSuggestionsBody> {
  const params = new URLSearchParams({
    field: input.field,
  });
  if (input.start) params.set("start", input.start);
  if (input.end) params.set("end", input.end);
  const q = input.q.trim();
  if (q) params.set("q", q);
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/suggestions?${params}`,
    "Failed to load call audio suggestions",
  );
  if (!Schema.is(CallAudioSuggestionsResponse)(body)) {
    throw new Error("Call audio suggestions response was malformed");
  }
  return body;
}

export function callAudioSuggestionsQueryOptions(input: {
  enabled: boolean;
  end: string;
  field: CallAudioSuggestionField;
  q: string;
  start: string;
}) {
  return queryOptions({
    enabled: input.enabled,
    queryFn: () => fetchCallAudioSuggestions(input),
    queryKey: [
      ...operatorsQueryKeys.all,
      "dashboards",
      "call-audio",
      "suggestions",
      input,
    ] as const,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

/** Loads transcript text for the open dialog. The list query never includes it. */
export async function fetchCallAudioTranscript(
  source: CallAudioSource,
  recordingId: string,
): Promise<CallAudioTranscriptBody> {
  const body = await readDashboardJson(
    `/api/v1/dashboards/call-audio/${encodeURIComponent(source)}/${encodeURIComponent(recordingId)}/transcript`,
    "Failed to load the transcript",
    true,
  );
  if (!Schema.is(CallAudioTranscript)(body)) {
    throw new Error("Call audio transcript response was malformed");
  }
  return body;
}

export function callAudioTranscriptQueryOptions(
  source: CallAudioSource,
  recordingId: string,
  enabled: boolean,
) {
  return queryOptions({
    enabled,
    gcTime: 0,
    queryFn: () => fetchCallAudioTranscript(source, recordingId),
    queryKey: [
      ...operatorsQueryKeys.all,
      "dashboards",
      "call-audio",
      "transcript",
      source,
      recordingId,
    ] as const,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
}

/** Mints a short-lived playback URL. The recording list never includes one. */
export async function fetchCallAudioPlayback(
  source: CallAudioSource,
  recordingId: string,
): Promise<CallAudioPlaybackBody> {
  const body = await readDashboardJson(
    `/api/v1/call-audio/${encodeURIComponent(source)}/${encodeURIComponent(recordingId)}/playback`,
    "Failed to prepare call audio",
  );
  if (!Schema.is(CallAudioPlayback)(body))
    throw new Error("Call audio playback response was malformed");
  return body;
}

function appendAll(params: URLSearchParams, key: string, values: readonly string[]) {
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed) params.append(key, trimmed);
  }
}

async function readDashboardJson(
  path: string,
  fallbackMessage: string,
  uncached = false,
  requireCallAudioApi = false,
): Promise<unknown> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
    cache: uncached ? "no-store" : "default",
    credentials: "include",
  });
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.status === 403) {
    throw new OperatorsForbiddenError("You do not have access to Dashboards.");
  }
  if (requireCallAudioApi && responseNeedsCallAudioReload(response)) {
    reloadCallAudioClient();
  }
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage));
  }
  rememberCallAudioApi(response);
  return await response.json();
}

function responseNeedsCallAudioReload(response: {
  headers?: { get?: (name: string) => string | null };
  status: number;
}): boolean {
  if (response.status !== 200 && response.status !== 404) return false;
  if (typeof response.headers?.get !== "function") return false;
  return response.headers.get(CALL_AUDIO_API_HEADER) !== CALL_AUDIO_API_VERSION;
}

function rememberCallAudioApi(response: {
  headers?: { get?: (name: string) => string | null };
}): void {
  if (typeof response.headers?.get !== "function") return;
  if (response.headers.get(CALL_AUDIO_API_HEADER) !== CALL_AUDIO_API_VERSION) return;
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(CALL_AUDIO_RELOAD_KEY);
}

function reloadCallAudioClient(): never {
  const storage = typeof sessionStorage === "undefined" ? null : sessionStorage;
  if (storage && storage.getItem(CALL_AUDIO_RELOAD_KEY) !== "1" && typeof window !== "undefined") {
    storage.setItem(CALL_AUDIO_RELOAD_KEY, "1");
    window.location.reload();
  }
  throw new Error("Call audio recordings response was malformed");
}
